/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type ComponentPropsWithoutRef,
    type ComponentType,
    forwardRef,
    lazy,
    Suspense,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { OptionalLazyErrorBoundary } from '@/components/optional-lazy-error-boundary';

export interface LazyCategoryRefinementsPanelProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
    open: boolean;
    result: ShopperSearch.schemas['ProductSearchResult'];
    refine: string[];
    /** Test seam for proving that the refinement graph loads only after interaction. */
    loadRefinements?: RefinementsModuleLoader;
}

interface CategoryRefinementsProps {
    result: ShopperSearch.schemas['ProductSearchResult'];
    refine: string[];
}

export type RefinementsModuleLoader = () => Promise<{ default: ComponentType<CategoryRefinementsProps> }>;

const loadCategoryRefinements: RefinementsModuleLoader = () => import('./index');

/**
 * Build a fresh `lazy()` wrapper. `attempt` is intentionally unused inside the body — its only job
 * is to be threaded through from the caller's `useMemo` deps so a retry (which bumps the attempt)
 * rebuilds the wrapper. React.lazy caches a rejected import on the wrapper, so only a new wrapper
 * re-attempts a failed load; the argument keeps `loadAttempt` a referenced (thus non-redundant)
 * dependency without a lint suppression.
 */
function createLazyRefinements(loadRefinements: RefinementsModuleLoader, _attempt: number) {
    return lazy(loadRefinements);
}

function RefinementsLoadingFallback() {
    const { t } = useTranslation();

    return (
        <div
            className="space-y-4 border border-border p-4"
            role="status"
            aria-label={t('categoryRefinements:loading', { defaultValue: 'Loading filters' })}>
            {Array.from({ length: 3 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
            ))}
        </div>
    );
}

function RefinementsErrorFallback({ onRetry }: { onRetry: () => void }) {
    const { t } = useTranslation();

    return (
        <div className="border border-border p-4" role="alert">
            <p className="text-sm text-muted-foreground">
                {t('categoryRefinements:loadError', { defaultValue: 'Filters could not load. Please try again.' })}
            </p>
            <Button className="mt-3" variant="outline" size="sm" onClick={onRetry}>
                {t('categoryRefinements:retry', { defaultValue: 'Retry' })}
            </Button>
        </div>
    );
}

/**
 * Keeps the full refinement controls out of the initial PLP/Search graph until the panel opens.
 * Bumping `loadAttempt` builds a fresh `lazy()` wrapper on retry, because React.lazy caches a
 * rejected import on the wrapper and only a new wrapper re-attempts it.
 *
 * Caveat: this defeats React's per-wrapper rejection cache, but not the browser's module map — if
 * the underlying `import()` fetch itself failed (e.g. a rotated chunk hash 404s for a stale client),
 * the engine may serve the cached rejection without a new request, so Retry can be a no-op for that
 * specific failure. The boundary still contains the crash; a hard reload is the reliable recovery.
 */
export const LazyCategoryRefinementsPanel = forwardRef<HTMLDivElement, LazyCategoryRefinementsPanelProps>(
    function LazyCategoryRefinementsPanel(
        { open, result, refine, loadRefinements = loadCategoryRefinements, ...panelProps },
        ref
    ) {
        const [loadAttempt, setLoadAttempt] = useState(0);
        // `loadAttempt` is the freshness key: bumping it recomputes the memo into a new lazy wrapper.
        const CategoryRefinements = useMemo(
            () => createLazyRefinements(loadRefinements, loadAttempt),
            [loadRefinements, loadAttempt]
        );

        if (!open) return null;

        return (
            <div ref={ref} {...panelProps}>
                <OptionalLazyErrorBoundary
                    resetKey={loadAttempt}
                    fallback={<RefinementsErrorFallback onRetry={() => setLoadAttempt((attempt) => attempt + 1)} />}>
                    <Suspense fallback={<RefinementsLoadingFallback />}>
                        <CategoryRefinements result={result} refine={refine} />
                    </Suspense>
                </OptionalLazyErrorBoundary>
            </div>
        );
    }
);
